/**
 * Thumb Click Fallback
 * 
 * Defensive event handler for product PDP thumbnails + main slideshow
 * + thumb strip scroll fallback for when Flickity fails to initialize
 * on the product gallery.
 * 
 * Background: On certain product configurations (specifically when one
 * flavor variant is fully out-of-stock and Motion theme's image-set 
 * variant filtering is active), Flickity fails to bind to the product
 * slideshow. Without Flickity, thumbnail clicks fall through to their
 * raw href, opening fullscreen images instead of swapping the main slide,
 * and the main image area has no swipe-navigation on mobile.
 * 
 * Behavior:
 * - Working pages (Flickity bound): no-op, native Flickity handles everything.
 * - Broken pages (Flickity not bound):
 *   1. Click handler manually scrolls to the matching main slide when
 *      a thumb is clicked.
 *   2. Thumb strip gets horizontal scroll enabled.
 *   3. Main slideshow gets horizontal scroll/swipe enabled with snap.
 *   4. Slides for hidden flavor variants are filtered out so users only
 *      swipe through the relevant images.
 *   5. As user swipes through main images, the thumb strip auto-scrolls
 *      to keep the active thumb visible and highlighted.
 * 
 * Safe to remove if the underlying Motion theme issue is ever fixed.
 */

(function() {
  'use strict';

  /* ============================================================
     CSS injection — only activates on pages where Flickity 
     didn't bind to the slideshow or thumb strip
     ============================================================ */
  var css = 
    /* Thumb strip scroll fallback */
    '[data-thumb-scroll-fallback="true"] { ' +
      'overflow-x: auto !important; ' +
      'overflow-y: hidden !important; ' +
      'scroll-behavior: smooth; ' +
      '-webkit-overflow-scrolling: touch; ' +
      'display: flex !important; ' +
      'flex-wrap: nowrap !important; ' +
      'gap: 8px; ' +
    '} ' +
    '[data-thumb-scroll-fallback="true"] .product__thumb-item { ' +
      'flex: 0 0 auto !important; ' +
    '} ' +
    '[data-thumb-scroll-fallback="true"] .product__thumb-item.hide { ' +
      'display: none !important; ' +
    '} ' +
    /* Active thumb visual indicator */
    // '[data-thumb-scroll-fallback="true"] .product__thumb-item[data-fallback-active="true"] .product__thumb { ' +
    //   'opacity: 1; ' +
    //   'outline: 2px solid #1a1a1a; ' +
    //   'outline-offset: 2px; ' +
    // '} ' +
    // '[data-thumb-scroll-fallback="true"] .product__thumb-item:not([data-fallback-active="true"]) .product__thumb { ' +
    //   'opacity: 0.7; ' +
    //   'transition: opacity 0.2s ease; ' +
    // '} ' +
    '[data-thumb-scroll-fallback="true"]::-webkit-scrollbar { ' +
      'height: 6px; ' +
    '} ' +
    '[data-thumb-scroll-fallback="true"]::-webkit-scrollbar-thumb { ' +
      'background: rgba(0,0,0,0.2); ' +
      'border-radius: 3px; ' +
    '} ' +
    '[data-thumb-scroll-fallback="true"]::-webkit-scrollbar-track { ' +
      'background: transparent; ' +
    '} ' +

    /* Main slideshow swipe fallback */
    '[data-slideshow-scroll-fallback="true"] { ' +
      'overflow-x: auto !important; ' +
      'overflow-y: hidden !important; ' +
      'scroll-behavior: smooth; ' +
      '-webkit-overflow-scrolling: touch; ' +
      'scroll-snap-type: x mandatory; ' +
      'display: flex !important; ' +
      'flex-wrap: nowrap !important; ' +
      'width: 100%; ' +
    '} ' +
    '[data-slideshow-scroll-fallback="true"] .product-main-slide { ' +
      'flex: 0 0 100% !important; ' +
      'width: 100% !important; ' +
      'scroll-snap-align: start; ' +
      'display: block !important; ' +
      'position: relative !important; ' +
      'left: auto !important; ' +
      'transform: none !important; ' +
      'opacity: 1 !important; ' +
      'visibility: visible !important; ' +
    '} ' +
    /* Hide slides that don't correspond to a visible thumb 
       (variant-specific slides for hidden flavors) */
    '[data-slideshow-scroll-fallback="true"] .product-main-slide[data-fallback-hidden="true"] { ' +
      'display: none !important; ' +
    '} ' +
    /* Hide scrollbar on the main slideshow — desktop users use thumbs, 
       mobile users swipe naturally without needing to see a scrollbar */
    '[data-slideshow-scroll-fallback="true"]::-webkit-scrollbar { ' +
      'display: none; ' +
    '} ' +
    '[data-slideshow-scroll-fallback="true"] { ' +
      '-ms-overflow-style: none; ' +
      'scrollbar-width: none; ' +
    '}';

  var styleTag = document.createElement('style');
  styleTag.setAttribute('data-thumb-fallback-css', 'true');
  styleTag.appendChild(document.createTextNode(css));
  document.head.appendChild(styleTag);

  /* ============================================================
     Helpers
     ============================================================ */

  function normalizeUrl(url) {
    if (!url) return '';
    return url
      .replace(/^https?:/, '')
      .replace(/^\/\//, '')
      .split('?')[0]
      .toLowerCase();
  }

  function getBaseFilename(url) {
    return normalizeUrl(url)
      .split('/').pop()
      .replace(/_\d+x\d+/, '');
  }

  function isFlickityBound(el) {
    if (!el) return false;
    if (typeof Flickity === 'undefined') return false;
    return !!Flickity.data(el);
  }

  function getSlideImageUrl(slide) {
    var img = slide.querySelector('img[data-photoswipe-src], img[src]');
    if (!img) return '';
    return normalizeUrl(
      img.getAttribute('data-photoswipe-src') || img.getAttribute('src')
    );
  }

  function getVisibleThumbUrls(thumbsContainer) {
    var urls = {};
    var visibleThumbAnchors = thumbsContainer.querySelectorAll(
      '.product__thumb-item:not(.hide) .product__thumb'
    );
    for (var i = 0; i < visibleThumbAnchors.length; i++) {
      var href = visibleThumbAnchors[i].getAttribute('href');
      if (href) {
        var url = normalizeUrl(href);
        urls[url] = true;
        urls[getBaseFilename(href)] = true;
      }
    }
    return urls;
  }

  // Find the thumb that matches a given image URL (exact or filename match)
  function findThumbForUrl(thumbsContainer, imageUrl) {
    var imageFilename = getBaseFilename(imageUrl);
    var thumbs = thumbsContainer.querySelectorAll('.product__thumb-item:not(.hide)');

    for (var i = 0; i < thumbs.length; i++) {
      var anchor = thumbs[i].querySelector('.product__thumb');
      if (!anchor) continue;
      var thumbHref = anchor.getAttribute('href');
      if (!thumbHref) continue;

      if (normalizeUrl(thumbHref) === imageUrl) return thumbs[i];
      if (getBaseFilename(thumbHref) === imageFilename) return thumbs[i];
    }
    return null;
  }

  // Highlight a specific thumb as active and scroll it into view in the strip
  function setActiveThumb(thumbsContainer, activeThumb) {
    if (!activeThumb) return;

    // Clear previous active state on all thumbs
    var allThumbs = thumbsContainer.querySelectorAll('.product__thumb-item');
    for (var i = 0; i < allThumbs.length; i++) {
      allThumbs[i].removeAttribute('data-fallback-active');
    }

    // Set new active state
    activeThumb.setAttribute('data-fallback-active', 'true');

    // Scroll the active thumb into view within the thumb strip,
    // but ONLY if it's not already fully visible (avoid jitter)
    var stripRect = thumbsContainer.getBoundingClientRect();
    var thumbRect = activeThumb.getBoundingClientRect();

    var fullyVisible = (
      thumbRect.left >= stripRect.left &&
      thumbRect.right <= stripRect.right
    );

    if (!fullyVisible) {
      activeThumb.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center'
      });
    }
  }

  // Determine which slide is currently in view based on scroll position,
  // then update the active thumb to match.
  function syncThumbToVisibleSlide(photosContainer, thumbsContainer) {
    try {
      var slides = photosContainer.querySelectorAll(
        '.product-main-slide:not([data-fallback-hidden="true"])'
      );
      if (slides.length === 0) return;

      // Find the slide whose center is closest to the container's center
      var containerRect = photosContainer.getBoundingClientRect();
      var containerCenter = containerRect.left + containerRect.width / 2;

      var closestSlide = null;
      var closestDistance = Infinity;

      for (var i = 0; i < slides.length; i++) {
        var slideRect = slides[i].getBoundingClientRect();
        var slideCenter = slideRect.left + slideRect.width / 2;
        var distance = Math.abs(slideCenter - containerCenter);

        if (distance < closestDistance) {
          closestDistance = distance;
          closestSlide = slides[i];
        }
      }

      if (!closestSlide) return;

      var slideUrl = getSlideImageUrl(closestSlide);
      var matchingThumb = findThumbForUrl(thumbsContainer, slideUrl);
      if (matchingThumb) {
        setActiveThumb(thumbsContainer, matchingThumb);
      }
    } catch (e) {
      return;
    }
  }

  // Debounce utility to avoid running scroll handler on every pixel of scroll
  function debounce(fn, delay) {
    var timeout;
    return function() {
      var context = this;
      var args = arguments;
      clearTimeout(timeout);
      timeout = setTimeout(function() {
        fn.apply(context, args);
      }, delay);
    };
  }

  /* ============================================================
     Apply fallbacks
     ============================================================ */

  function applyThumbStripScrollFallback(section) {
    try {
      var thumbsContainer = section.querySelector('[data-product-thumbs]');
      if (!thumbsContainer) return;
      if (isFlickityBound(thumbsContainer)) return;
      thumbsContainer.setAttribute('data-thumb-scroll-fallback', 'true');
    } catch (e) {
      return;
    }
  }

  function applySlideshowScrollFallback(section) {
    try {
      var photosContainer = section.querySelector('.product-slideshow');
      if (!photosContainer) return;
      if (isFlickityBound(photosContainer)) return;

      var thumbsContainer = section.querySelector('[data-product-thumbs]');
      if (!thumbsContainer) return;

      photosContainer.setAttribute('data-slideshow-scroll-fallback', 'true');

      // Hide variant-specific slides that don't correspond to visible thumbs
      var visibleUrls = getVisibleThumbUrls(thumbsContainer);
      var slides = photosContainer.querySelectorAll('.product-main-slide');

      for (var i = 0; i < slides.length; i++) {
        var slideUrl = getSlideImageUrl(slides[i]);
        var slideFilename = getBaseFilename(slideUrl);

        if (visibleUrls[slideUrl] || visibleUrls[slideFilename]) {
          slides[i].removeAttribute('data-fallback-hidden');
        } else {
          slides[i].setAttribute('data-fallback-hidden', 'true');
        }
      }

      // Wire up scroll listener: as user swipes through main images,
      // sync the active thumb and scroll the thumb strip to match.
      var debouncedSync = debounce(function() {
        syncThumbToVisibleSlide(photosContainer, thumbsContainer);
      }, 100);

      photosContainer.addEventListener('scroll', debouncedSync, { passive: true });

      // Set initial active thumb based on starting slide
      setTimeout(function() {
        syncThumbToVisibleSlide(photosContainer, thumbsContainer);
      }, 100);
    } catch (e) {
      return;
    }
  }

  /* ============================================================
     Click handler — runs in capture phase to fire before any
     bubble-phase handlers from the theme
     ============================================================ */
  document.addEventListener('click', function(evt) {
    var thumbAnchor = evt.target.closest('.product__thumb');
    if (!thumbAnchor) return;

    var thumbItem = thumbAnchor.closest('.product__thumb-item');
    if (!thumbItem) return;
    if (thumbItem.classList.contains('hide')) return;

    try {
      var thumbsContainer = thumbAnchor.closest('[data-product-thumbs]');
      if (!thumbsContainer) return;

      var section = thumbsContainer.closest('[data-section-type="product"]');
      if (!section) return;

      var photosContainer = section.querySelector('.product-slideshow');
      if (!photosContainer) return;

      // If Flickity is bound, let it handle the click.
      if (isFlickityBound(photosContainer)) return;

      // Flickity not bound — fallback path.
      evt.preventDefault();

      var thumbHref = thumbAnchor.getAttribute('href');
      if (!thumbHref) return;
      var thumbUrl = normalizeUrl(thumbHref);
      var thumbFilename = getBaseFilename(thumbHref);

      // Find the slide whose image matches the thumb's URL
      var slides = photosContainer.querySelectorAll('.product-main-slide');
      var targetSlide = null;

      for (var i = 0; i < slides.length; i++) {
        if (getSlideImageUrl(slides[i]) === thumbUrl) {
          targetSlide = slides[i];
          break;
        }
      }

      if (!targetSlide) {
        for (var k = 0; k < slides.length; k++) {
          if (getBaseFilename(getSlideImageUrl(slides[k])) === thumbFilename) {
            targetSlide = slides[k];
            break;
          }
        }
      }

      if (!targetSlide) return;

      // Smooth-scroll the slide into view
      if (photosContainer.getAttribute('data-slideshow-scroll-fallback') === 'true') {
        targetSlide.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'nearest', 
          inline: 'start' 
        });
      } else {
        for (var j = 0; j < slides.length; j++) {
          slides[j].style.display = (slides[j] === targetSlide) ? 'block' : 'none';
        }
      }

      // Update active thumb visual state immediately (don't wait for scroll listener)
      setActiveThumb(thumbsContainer, thumbItem);

    } catch (e) {
      return;
    }
  }, true);

  /* ============================================================
     Initialize fallbacks on page load
     ============================================================ */
  function initFallbacks() {
    var productSections = document.querySelectorAll('[data-section-type="product"]');
    for (var i = 0; i < productSections.length; i++) {
      applyThumbStripScrollFallback(productSections[i]);
      applySlideshowScrollFallback(productSections[i]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      setTimeout(initFallbacks, 500);
    });
  } else {
    setTimeout(initFallbacks, 500);
  }

})();